import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { adminAPI } from '../../services/api';
import LoadingSpinner from '../../components/LoadingSpinner';
import PageLayout from '../../components/PageLayout';
import toast from 'react-hot-toast';
import {
  CreditCardIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  Cog6ToothIcon,
  ArrowPathIcon,
  LinkIcon,
  KeyIcon,
  DocumentTextIcon,
  CalendarDaysIcon,
} from '@heroicons/react/24/outline';
import QuoteTermsEditor, {
  QuoteTermsShape,
  QuoteTermsSummary,
  compactQuoteTermsOverride,
  getDefaultQuoteTerms,
  mergeQuoteTerms,
} from '../../components/quotes/QuoteTermsEditor';

const EVENT_TERM_TYPES = [
  'wedding',
  'charter',
  'corporate_event',
  'airport_transfer',
  'conference_shuttle',
  'prom_homecoming',
  'sports_vip',
] as const;

type EventTermType = typeof EVENT_TERM_TYPES[number];

const EVENT_TERM_LABELS: Record<EventTermType, string> = {
  wedding: 'Wedding',
  charter: 'Charter',
  corporate_event: 'Corporate event',
  airport_transfer: 'Airport transfer',
  conference_shuttle: 'Conference shuttle',
  prom_homecoming: 'Prom / homecoming',
  sports_vip: 'Sports VIP',
};

function AdminSettings() {
  const isPlatformHost =
    typeof window !== 'undefined' &&
    window.location?.hostname?.toLowerCase().startsWith('globaladmin.');
  const [activeTab, setActiveTab] = useState(isPlatformHost ? 'general' : 'stripe');
  const [generalSettings, setGeneralSettings] = useState({
    companyName: 'ModalOS',
    companyAddress: '',
    taxId: '',
    supportEmail: 'support@modalhub.net',
    supportPhone: '',
    invoicePrefix: 'INV',
    timezone: 'America/New_York'
  });
  const [quoteTerms, setQuoteTerms] = useState<QuoteTermsShape>(getDefaultQuoteTerms());
  const [eventTerms, setEventTerms] = useState<Partial<Record<EventTermType, QuoteTermsShape | null>>>({});
  const [systemEventTerms, setSystemEventTerms] = useState<Partial<Record<EventTermType, QuoteTermsShape>>>({});
  const [selectedEventType, setSelectedEventType] = useState<EventTermType>('wedding');

  const queryClient = useQueryClient();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tab = params.get('tab');
    if (tab && ['general', 'stripe', 'quoteTerms', 'security'].includes(tab)) {
      setActiveTab(tab);
    }
  }, []);

  // Load general settings from backend
  useQuery(
    'admin-general-settings',
    () => adminAPI.getGeneralSettings(),
    {
      select: (response) => response.data,
      onSuccess: (data) => {
        setGeneralSettings({
          companyName: data.companyName || '',
          companyAddress: data.companyAddress || '',
          taxId: data.taxId || '',
          supportEmail: data.supportEmail || '',
          supportPhone: data.supportPhone || '',
          invoicePrefix: data.invoicePrefix || 'INV',
          timezone: data.timezone || 'America/New_York',
        });
      },
    }
  );

  const { isLoading: loadingQuoteTerms } = useQuery(
    'admin-quote-terms',
    () => adminAPI.getQuoteTerms(),
    {
      select: (response) => response.data,
      onSuccess: (data) => {
        setQuoteTerms(mergeQuoteTerms(data.quoteTerms));
      },
    }
  );

  const { isLoading: loadingEventTerms } = useQuery(
    'admin-event-terms',
    () => adminAPI.getEventTerms(),
    {
      select: (response) => response.data,
      onSuccess: (data) => {
        setEventTerms(data.eventTerms || {});
        setSystemEventTerms(data.systemDefaults || {});
      },
    }
  );

  // Save general settings mutation
  const saveGeneralMutation = useMutation(
    () => adminAPI.updateGeneralSettings(generalSettings),
    {
      onSuccess: () => {
        toast.success('General settings saved');
        queryClient.invalidateQueries('admin-general-settings');
      },
      onError: () => {
        toast.error('Failed to save general settings');
      },
    }
  );

  const saveQuoteTermsMutation = useMutation(
    () => adminAPI.updateQuoteTerms(quoteTerms),
    {
      onSuccess: () => {
        toast.success('Quote defaults saved');
        queryClient.invalidateQueries('admin-quote-terms');
      },
      onError: () => {
        toast.error('Failed to save quote defaults');
      },
    }
  );

  const saveEventTermsMutation = useMutation(
    () => {
      const compacted = EVENT_TERM_TYPES.reduce((next, eventType) => {
        const override = compactQuoteTermsOverride(eventTerms[eventType], quoteTerms);
        if (override) {
          next[eventType] = override;
        }
        return next;
      }, {} as Partial<Record<EventTermType, QuoteTermsShape>>);

      return adminAPI.updateEventTerms(compacted);
    },
    {
      onSuccess: () => {
        toast.success('Event templates saved');
        queryClient.invalidateQueries('admin-event-terms');
      },
      onError: () => {
        toast.error('Failed to save event templates');
      },
    }
  );

  // Check for OAuth callback result in URL — backend already exchanged the code
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const isCallback = params.get('stripe_callback');
    const isError = params.get('stripe_error');

    if (isCallback) {
      toast.success('Stripe account connected successfully!');
      queryClient.invalidateQueries('stripe-connect-status');
      window.history.replaceState({}, '', window.location.pathname);
    } else if (isError) {
      toast.error('Failed to connect Stripe account');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [queryClient]);

  // Fetch Stripe Connect status
  const { data: connectStatus, isLoading: statusLoading } = useQuery(
    'stripe-connect-status',
    () => adminAPI.getStripeConnectStatus(),
    { select: (response) => response.data }
  );
  const { data: connectHealth } = useQuery(
    'stripe-connect-health',
    () => adminAPI.getStripeConnectHealth(),
    { select: (response) => response.data },
  );
  const { data: platformFeePolicy } = useQuery(
    'admin-platform-fee-policy',
    () => adminAPI.getPlatformFeePolicy(),
    { select: (response) => response.data },
  );
  const effectivePlatformFee = platformFeePolicy?.effective;
  const effectivePlatformFeePercent = effectivePlatformFee?.rate_percent;
  const effectivePlatformFeeSource = effectivePlatformFee?.source === 'tenant_custom'
    ? 'custom tenant rate'
    : 'inherited platform default';
  const effectivePlatformFeeDate = effectivePlatformFee?.effective_from
    ? new Date(effectivePlatformFee.effective_from).toLocaleDateString()
    : null;

  const refreshMutation = useMutation(
    () => adminAPI.refreshStripeConnectStatus(),
    {
      onSuccess: () => {
        toast.success('Account status refreshed');
        queryClient.invalidateQueries('stripe-connect-status');
        queryClient.invalidateQueries('stripe-connect-health');
      },
      onError: () => {
        toast.error('Failed to refresh status');
      }
    }
  );

  const handleConnectStripe = async () => {
    try {
      const response = await adminAPI.getStripeConnectOAuthLink();
      window.location.href = response.data.url;
    } catch {
      toast.error('Failed to generate Stripe Connect link');
    }
  };

  // Access requests (company admin)
  const { data: accessRequests = [], isLoading: accessLoading } = useQuery(
    'admin-access-requests',
    () => adminAPI.getAccessRequests(),
    { select: (response) => response.data }
  );

  const approveMutation = useMutation(
    (id: string) => adminAPI.approveAccessRequest(id),
    {
      onSuccess: () => {
        toast.success('Access request approved');
        queryClient.invalidateQueries('admin-access-requests');
      },
      onError: () => { toast.error('Failed to approve request'); },
    }
  );

  const denyMutation = useMutation(
    (data: { id: string; reason?: string }) => adminAPI.denyAccessRequest(data.id, data.reason),
    {
      onSuccess: () => {
        toast.success('Access request denied');
        queryClient.invalidateQueries('admin-access-requests');
      },
      onError: () => { toast.error('Failed to deny request'); },
    }
  );

  const [denyReason, setDenyReason] = useState('');
  const [denyingId, setDenyingId] = useState<string | null>(null);

  const allTabs = [
    { id: 'stripe', label: 'Stripe Connect', icon: CreditCardIcon, platformHidden: true },
    { id: 'general', label: 'General Settings', icon: Cog6ToothIcon },
    { id: 'quoteTerms', label: 'Quote Defaults', icon: DocumentTextIcon },
    { id: 'eventTerms', label: 'Event Templates', icon: CalendarDaysIcon },
    // { id: 'security', label: 'Security', icon: ShieldCheckIcon },
    { id: 'access', label: 'Access', icon: KeyIcon },
  ];
  const tabs = isPlatformHost ? allTabs.filter(t => !t.platformHidden) : allTabs;

  return (
    <PageLayout>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-on-surface">System Settings</h1>
        <p className="mt-2 text-on-surface-variant">Configure payment processing and system preferences</p>
      </div>

      {/* Tab Navigation */}
      <div className="mb-8">
        <div>
          <nav className="-mb-px flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                  activeTab === tab.id
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-on-surface-variant hover:text-on-surface hover:border-gray-300'
                }`}
              >
                <tab.icon className="h-5 w-5" />
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Stripe Connect Tab */}
      {activeTab === 'stripe' && (
        <div className="space-y-6">
          {/* Connection Status */}
          <div className="card p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-on-surface">Stripe Connect</h3>
              {statusLoading ? (
                <LoadingSpinner size="sm" />
              ) : connectStatus?.accessRevoked ? (
                <div className="flex items-center space-x-2">
                  <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />
                  <span className="text-red-700 font-medium">Access Revoked</span>
                </div>
              ) : connectStatus?.connected ? (
                <div className="flex items-center space-x-2">
                  <CheckCircleIcon className="h-5 w-5 text-green-500" />
                  <span className="text-green-700 font-medium">Connected</span>
                </div>
              ) : (
                <div className="flex items-center space-x-2">
                  <ExclamationTriangleIcon className="h-5 w-5 text-orange-500" />
                  <span className="text-orange-700 font-medium">Not Connected</span>
                </div>
              )}
            </div>

            <p className="text-sm text-on-surface-variant mb-6">
              Connect your Stripe account to accept payments from customers. {typeof effectivePlatformFeePercent === 'number'
                ? `Your current ModalOS platform fee is ${effectivePlatformFeePercent}% on the applicable transaction basis. `
                : 'Your applicable ModalOS platform fee will appear after the policy loads. '}
              Stripe processing fees remain separate.
            </p>

            {statusLoading ? (
              <LoadingSpinner size="sm" />
            ) : connectStatus?.connected || connectStatus?.accessRevoked ? (
              <div>
                {/* Account Details */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div>
                    <p className="text-sm font-medium text-on-surface-variant">Account ID</p>
                    <p className="text-sm text-on-surface font-mono">{connectStatus.accountId}</p>
                  </div>
                  {connectStatus.businessName && (
                    <div>
                      <p className="text-sm font-medium text-on-surface-variant">Business Name</p>
                      <p className="text-sm text-on-surface">{connectStatus.businessName}</p>
                    </div>
                  )}
                  {connectStatus.email && (
                    <div>
                      <p className="text-sm font-medium text-on-surface-variant">Email</p>
                      <p className="text-sm text-on-surface">{connectStatus.email}</p>
                    </div>
                  )}
                  {connectStatus.country && (
                    <div>
                      <p className="text-sm font-medium text-on-surface-variant">Country</p>
                      <p className="text-sm text-on-surface">{connectStatus.country}</p>
                    </div>
                  )}
                  {connectHealth && (
                    <div>
                      <p className="text-sm font-medium text-on-surface-variant">Account Health</p>
                      <p className="text-sm font-semibold text-on-surface">{connectHealth.effective_status || connectHealth.status}</p>
                      <p className="mt-1 text-xs text-on-surface-variant">
                        {connectHealth.status_reasons?.join(', ') || 'No current issues reported'}
                      </p>
                    </div>
                  )}
                </div>

                {/* Capabilities */}
                <div className="flex flex-wrap gap-3 mb-6">
                  <div className={`inline-flex items-center px-3 py-1 rounded-pill text-sm ${
                    connectStatus.chargesEnabled ? 'bg-success-container text-success-on-container' : 'bg-error-container text-error-on-container'
                  }`}>
                    {connectStatus.chargesEnabled ? 'Charges Enabled' : 'Charges Disabled'}
                  </div>
                  <div className={`inline-flex items-center px-3 py-1 rounded-pill text-sm ${
                    connectStatus.payoutsEnabled ? 'bg-success-container text-success-on-container' : 'bg-error-container text-error-on-container'
                  }`}>
                    {connectStatus.payoutsEnabled ? 'Payouts Enabled' : 'Payouts Disabled'}
                  </div>
                  {connectStatus.detailsSubmitted !== undefined && (
                    <div className={`inline-flex items-center px-3 py-1 rounded-pill text-sm ${
                      connectStatus.detailsSubmitted ? 'bg-success-container text-success-on-container' : 'bg-warning-container text-warning-on-container'
                    }`}>
                      {connectStatus.detailsSubmitted ? 'Details Submitted' : 'Onboarding Incomplete'}
                    </div>
                  )}
                  {connectStatus.accessRevoked && (
                    <div className="inline-flex items-center px-3 py-1 rounded-pill text-sm bg-error-container text-error-on-container">
                      Stripe Access Revoked
                    </div>
                  )}
                </div>

                {connectStatus.accessRevoked && (
                  <div className="mb-6 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                    Stripe access was revoked from Stripe. ModalOS preserved this account ID for historical customers and invoices; contact Modal support before reconnecting or replacing it.
                  </div>
                )}

                {/* Actions */}
                <div className="flex space-x-3">
                  {!connectStatus.accessRevoked && (
                    <button
                      onClick={() => refreshMutation.mutate()}
                      disabled={refreshMutation.isLoading}
                      className="btn-secondary flex items-center space-x-2"
                    >
                      {refreshMutation.isLoading ? <LoadingSpinner size="sm" /> : <ArrowPathIcon className="h-4 w-4" />}
                      <span>Refresh Status</span>
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div>
                <button
                  onClick={handleConnectStripe}
                  className="btn-primary flex items-center space-x-2"
                >
                  <LinkIcon className="h-4 w-4" />
                  <span>Connect with Stripe</span>
                </button>
              </div>
            )}
          </div>

          {/* How it works */}
          <div className="card p-6">
            <h3 className="text-lg font-medium text-on-surface mb-4">How Stripe Connect Works</h3>
            <div className="space-y-4 text-sm text-on-surface-variant">
              <div className="flex items-start space-x-3">
                <span className="flex-shrink-0 w-6 h-6 bg-info-container text-info-on-container rounded-full flex items-center justify-center text-xs font-bold">1</span>
                <div>
                  <p className="font-medium text-on-surface">Connect Your Stripe Account</p>
                  <p>Click the button above to securely connect the Stripe account selected during onboarding. After connection, account changes are handled with Modal support.</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <span className="flex-shrink-0 w-6 h-6 bg-info-container text-info-on-container rounded-full flex items-center justify-center text-xs font-bold">2</span>
                <div>
                  <p className="font-medium text-on-surface">Customers Pay Through Your Account</p>
                  <p>When customers pay invoices, payments are processed through your connected Stripe account.</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <span className="flex-shrink-0 w-6 h-6 bg-info-container text-info-on-container rounded-full flex items-center justify-center text-xs font-bold">3</span>
                <div>
                  <p className="font-medium text-on-surface">Automatic Fee & Payout</p>
                  <p>{typeof effectivePlatformFeePercent === 'number'
                    ? `ModalOS applies your ${effectivePlatformFeeSource} of ${effectivePlatformFeePercent}%${effectivePlatformFeeDate ? `, effective ${effectivePlatformFeeDate}` : ''}. `
                    : 'ModalOS applies the effective platform fee shown for your tenant. '}
                  Stripe processing fees and payout timing remain governed by your connected account.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* General Settings Tab */}
      {activeTab === 'general' && (
        <div className="card p-6">
          <h3 className="text-lg font-medium text-on-surface mb-6">General Settings</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-on-surface-variant mb-1">Company Name</label>
              <input
                type="text"
                className="input-field"
                value={generalSettings.companyName}
                onChange={(e) => setGeneralSettings({ ...generalSettings, companyName: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-on-surface-variant mb-1">Support Email</label>
              <input
                type="email"
                className="input-field"
                value={generalSettings.supportEmail}
                onChange={(e) => setGeneralSettings({ ...generalSettings, supportEmail: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-on-surface-variant mb-1">Support Phone</label>
              <input
                type="tel"
                className="input-field"
                value={generalSettings.supportPhone}
                onChange={(e) => setGeneralSettings({ ...generalSettings, supportPhone: e.target.value })}
                placeholder="(210) 225-5466"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-on-surface-variant mb-1">Invoice Prefix</label>
              <input
                type="text"
                className="input-field"
                value={generalSettings.invoicePrefix}
                onChange={(e) => setGeneralSettings({ ...generalSettings, invoicePrefix: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-on-surface-variant mb-1">Timezone</label>
              <select
                className="input-field"
                value={generalSettings.timezone}
                onChange={(e) => setGeneralSettings({ ...generalSettings, timezone: e.target.value })}
              >
                <option value="America/New_York">Eastern Time</option>
                <option value="America/Chicago">Central Time</option>
                <option value="America/Denver">Mountain Time</option>
                <option value="America/Los_Angeles">Pacific Time</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-on-surface-variant mb-1">Company Address</label>
              <textarea
                className="input-field"
                rows={3}
                value={generalSettings.companyAddress}
                onChange={(e) => setGeneralSettings({ ...generalSettings, companyAddress: e.target.value })}
                placeholder="Enter your company address for invoices..."
              />
            </div>
          </div>
          <div className="mt-6">
            <button
              className="btn-primary"
              onClick={() => saveGeneralMutation.mutate()}
              disabled={saveGeneralMutation.isLoading}
            >
              {saveGeneralMutation.isLoading ? 'Saving...' : 'Save General Settings'}
            </button>
          </div>
        </div>
      )}

      {activeTab === 'quoteTerms' && (
        <div className="space-y-5">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h3 className="text-lg font-semibold text-on-surface">Quote Defaults</h3>
              <p className="mt-1 text-sm text-on-surface-variant">Tenant baseline for deposits, expiration, charges, and cancellation terms.</p>
            </div>
            <button
              type="button"
              className="btn-primary"
              onClick={() => saveQuoteTermsMutation.mutate()}
              disabled={saveQuoteTermsMutation.isLoading || loadingQuoteTerms}
            >
              {saveQuoteTermsMutation.isLoading ? 'Saving...' : 'Save Defaults'}
            </button>
          </div>

          {loadingQuoteTerms ? (
            <div className="card p-6">
              <LoadingSpinner />
            </div>
          ) : (
            <>
              <QuoteTermsSummary terms={quoteTerms} />
              <div className="card p-6">
                <QuoteTermsEditor value={quoteTerms} onChange={setQuoteTerms} />
              </div>
            </>
          )}
        </div>
      )}

      {activeTab === 'eventTerms' && (
        <div className="space-y-5">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h3 className="text-lg font-semibold text-on-surface">Event Templates</h3>
              <p className="mt-1 text-sm text-on-surface-variant">Special-event policies layered on top of the tenant quote defaults.</p>
            </div>
            <button
              type="button"
              className="btn-primary"
              onClick={() => saveEventTermsMutation.mutate()}
              disabled={saveEventTermsMutation.isLoading || loadingEventTerms}
            >
              {saveEventTermsMutation.isLoading ? 'Saving...' : 'Save Templates'}
            </button>
          </div>

          {loadingEventTerms ? (
            <div className="card p-6">
              <LoadingSpinner />
            </div>
          ) : (
            <div className="grid gap-5 lg:grid-cols-[280px_minmax(0,1fr)]">
              <div className="space-y-2">
                {EVENT_TERM_TYPES.map((eventType) => {
                  const selected = selectedEventType === eventType;
                  const effective = mergeQuoteTerms(eventTerms[eventType] || systemEventTerms[eventType] || null, quoteTerms);
                  const hasTenantOverride = Boolean(compactQuoteTermsOverride(eventTerms[eventType], quoteTerms));

                  return (
                    <button
                      key={eventType}
                      type="button"
                      onClick={() => setSelectedEventType(eventType)}
                      className={`w-full rounded-card border p-3 text-left transition-colors ${
                        selected
                          ? 'border-primary-500 bg-primary-50 text-on-surface'
                          : 'border-outline/60 bg-surface hover:bg-surface-container-low'
                      }`}
                    >
                      <span className="block text-sm font-semibold">{EVENT_TERM_LABELS[eventType]}</span>
                      <span className="mt-1 block text-xs text-on-surface-variant">
                        {hasTenantOverride ? 'Tenant override' : systemEventTerms[eventType] ? 'System template' : 'Tenant default'}
                      </span>
                      <span className="mt-2 block text-xs text-on-surface-variant">
                        {effective.deposit?.type === 'none'
                          ? 'No deposit'
                          : `${Number(effective.deposit?.value || 0).toFixed(0)}${effective.deposit?.type === 'fixed' ? ' fixed' : '%'} deposit`}
                      </span>
                    </button>
                  );
                })}
              </div>

              <div className="space-y-4">
                <div className="card p-5">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <h4 className="text-base font-semibold text-on-surface">{EVENT_TERM_LABELS[selectedEventType]}</h4>
                      <p className="mt-1 text-sm text-on-surface-variant">
                        Effective terms for this event type before quote-level changes.
                      </p>
                    </div>
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => {
                        setEventTerms((current) => ({
                          ...current,
                          [selectedEventType]: null,
                        }));
                      }}
                    >
                      Reset Template
                    </button>
                  </div>
                  <div className="mt-4">
                    <QuoteTermsSummary
                      compact
                      terms={eventTerms[selectedEventType] || systemEventTerms[selectedEventType] || null}
                      fallback={quoteTerms}
                    />
                  </div>
                </div>

                <div className="card p-6">
                  <QuoteTermsEditor
                    value={eventTerms[selectedEventType] || systemEventTerms[selectedEventType] || null}
                    fallback={quoteTerms}
                    inheritanceMode
                    onChange={(next) => {
                      setEventTerms((current) => ({
                        ...current,
                        [selectedEventType]: next,
                      }));
                    }}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Security Tab */}
      {activeTab === 'security' && (
        <div className="card p-6">
          <h3 className="text-lg font-medium text-on-surface mb-6">Security Settings</h3>
          <p className="text-on-surface-variant">Security settings will be implemented in future versions.</p>
        </div>
      )}

      {/* Access Tab */}
      {activeTab === 'access' && (
        <div className="card p-6">
          <h3 className="text-lg font-medium text-on-surface mb-6">Access Requests</h3>
          <p className="text-sm text-on-surface-variant mb-6">
            Platform administrators can request access to your company instance. Review and manage requests below.
          </p>

          {accessLoading ? (
            <LoadingSpinner />
          ) : accessRequests.length === 0 ? (
            <div className="text-center py-8">
              <KeyIcon className="h-12 w-12 text-on-surface-variant mx-auto mb-2" />
              <p className="text-sm text-on-surface-variant">No pending access requests</p>
            </div>
          ) : (
            <div className="space-y-4">
              {accessRequests.map((req: any) => (
                <div key={req.id} className="flex items-center justify-between p-4 rounded-card bg-surface-container-low">
                  <div>
                    <p className="text-sm font-medium text-on-surface">{req.requester_email}</p>
                    <p className="text-xs text-on-surface-variant">Requested {new Date(req.created_at).toLocaleString()}</p>
                  </div>
                  <div className="flex items-center space-x-2">
                    {denyingId === req.id ? (
                      <div className="flex items-center space-x-2">
                        <input
                          type="text"
                          placeholder="Reason (optional)"
                          className="input-field text-sm w-48"
                          value={denyReason}
                          onChange={(e) => setDenyReason(e.target.value)}
                        />
                        <button
                          onClick={() => {
                            denyMutation.mutate({ id: req.id, reason: denyReason || undefined });
                            setDenyingId(null);
                            setDenyReason('');
                          }}
                          className="px-3 py-1.5 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded-md"
                        >
                          Confirm Deny
                        </button>
                        <button
                          onClick={() => { setDenyingId(null); setDenyReason(''); }}
                          className="px-3 py-1.5 text-xs font-medium text-on-surface-variant bg-surface-container-low hover:bg-surface-container-low rounded-md"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <>
                        <button
                          onClick={() => approveMutation.mutate(req.id)}
                          disabled={approveMutation.isLoading}
                          className="px-3 py-1.5 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded-md"
                        >
                          Approve
                        </button>
                        <button
                          onClick={() => setDenyingId(req.id)}
                          className="px-3 py-1.5 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded-md"
                        >
                          Deny
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </PageLayout>
  );
}

export default AdminSettings;
